import { filter, map, Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { DefaultContext } from '@apollo/client/core/types';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import {
  Component,
  Inject,
  inject,
  Input,
  OnChanges,
  Self,
  SimpleChanges,
} from '@angular/core';

import { Supplier, Suppliers } from './supplier-input.types';

import { DetailsBoxComponent } from '@iss/wo-common/components';
import { createUserContextHeader, debugUserContextHeader, WO_CONTEXT_HEADER } from '@iss/libs/user-context';
import { FilteredSuppliersGQL, Locale, Maybe, Role } from '@iss/libs/graphql';
import { TypeaheadSelectComponent, TypeaheadSelectItemDirective } from '@iss/core/typeahead-select';
import { Control, NOOP_VALUE_ACCESSOR } from '@iss/core/forms';

@Component({
  selector: 'wo-supplier-input',
  templateUrl: './supplier-input.component.html',
  standalone: true,
  imports: [
    DetailsBoxComponent,
    ReactiveFormsModule,
    TranslateModule,
    TypeaheadSelectComponent,
    TypeaheadSelectItemDirective,
  ],
})
export class SupplierInputComponent implements OnChanges {
  private readonly query = inject(FilteredSuppliersGQL);

  public loading$!: Observable<boolean>;
  public items$!: Observable<Suppliers>;

  public readonly stringify = (suppliers: Suppliers | null): (id: Supplier['id'] | 'ALL') => string =>
    id => id === 'ALL'
      ? 'All'
      : suppliers?.find(supplier => supplier.id === id)?.organization.name || '';

  public readonly matcher = (supplier: Supplier): string => supplier.organization.name;

  public readonly content = (id: Supplier['id'] | 'ALL', suppliers: Suppliers | null): string | undefined =>
    id === 'ALL'
      ? 'All'
      : suppliers?.find(supplier => supplier.id === id)?.organization.name;

  public readonly identity = (supplier: Supplier): Supplier['id'] => supplier.id;

  public constructor(
    @Self() @Inject(NgControl) public readonly control: Control<FormControl<Maybe<Supplier['id'] | 'ALL'>>>,
  ) {
    this.control.valueAccessor = NOOP_VALUE_ACCESSOR;
  }

  @Input() public withAll: Maybe<boolean>;
  @Input() public readonly: Maybe<boolean>;
  @Input() public placeholder: Maybe<string>;
  @Input() public available: Maybe<readonly Supplier['id'][]>;
  @Input() public role: Maybe<Role>;
  @Input() public country: Maybe<Locale>;
  @Input() public isTreatmentFacility: Maybe<boolean>;
  @Input() public multi: Maybe<boolean>;
  @Input() public loading: Maybe<boolean>;
  @Input() public forceTypeahead: Maybe<boolean>;
  @Input() public clearable = true;
  @Input() public secondaryLabelFn: (supplier: Supplier['id']) => Maybe<string> = () => null;

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.items$ || 'available' in changes || 'role' in changes || 'country' in changes || 'isTreatmentFacility' in changes) {
      this.initQuery();
    }
  }

  private initQuery(): void {
    const query$ = this.query.watch({
      suppliersFiltersInput: {
        offset: 0,
        limit: 999,
        supplierIds: this.available,
        country: this.country ? [ this.country ] : undefined,
        isTreatmentFacility: this.isTreatmentFacility,
      },
    }, {
      fetchPolicy: this.role ? 'no-cache' : undefined,
      context: this.context,
    }).valueChanges;

    this.loading$ = query$.pipe(map(({ loading }) => loading));
    this.items$ = query$.pipe(
      filter(({ data }) => Boolean(data)),
      map(({ data }) => data.suppliers.items),
    );
  }

  private get context(): DefaultContext | undefined {
    if (!this.role) return;

    const userContextHeader = createUserContextHeader({ role: this.role, country: this.country });
    debugUserContextHeader('supplier-input', userContextHeader);

    return {
      headers: {
        [WO_CONTEXT_HEADER]: userContextHeader,
      },
    };
  }
}
